const { Bezier } = require('bezier-js');

export interface SegmentVert
{
    x: number,
    y: number,
    cx: number,
    cy: number
}

export interface SegmentPoint
{
    x: number,
    y: number,
    t: number,
    nx: number,
    ny: number,
    a: number,
}

export class Segment
{
    /**
     * Get lines out of vertices. Each pair of vertices will be converted into a bezier line.
     * @param verts - List of vertices
     * @param absControl - Set to true if cx and cy values are absolute coordinates
     * @returns
     */
    public static getLines(verts: SegmentVert[], absControl = false): Bezier[]
    {
        const lines = [] as Bezier[];

        for (let i = 1; i < verts.length; i++)
        {
            const prev = verts[i - 1];
            const vert = verts[i];
            const prevcx = absControl ? prev.cx : prev.cx + prev.x;
            const prevcy = absControl ? prev.cy : prev.cy + prev.y;
            const vertcx = absControl ? vert.cx : vert.cx + vert.x;
            const vertcy = absControl ? vert.cy : vert.cy + vert.y;

            const apx = prev.x;
            const apy = prev.y;
            const acx = prevcx;
            const acy = prevcy;

            const bpx = vert.x;
            const bpy = vert.y;
            const bcx = (vert.x - (vertcx - vert.x));
            const bcy = vert.y - (vertcy - vert.y);

            const line = new Bezier(
                apx, apy, acx, acy,
                bcx, bcy, bpx, bpy,
            );

            lines.push(line);
        }

        return lines;
    }

    public static getLinesLength(lines: Bezier[]): number
    {
        let length = 0;

        for (const line of lines) length += line.length();

        return length;
    }

    /**
     * Get list points from a line
     * @param line - Bezier line to provide points
     * @param precision - Amount of points to be extracted
     * @param removeColinear - Minimum angle for a point to be considered 'colinear' to the previous, ang get removed if so.
     * @returns
     */
    public static getPointsFromLine2(line: Bezier, precision = 20, removeColinear = 0): SegmentPoint[]
    {
        const resultPoints = [] as SegmentPoint[];
        const doublePI = Math.PI * 2;
        const linePoints = line.getLUT(precision);

        let last: SegmentPoint | null = null;
        let lastAngle = Number.POSITIVE_INFINITY;

        for (let i = 0; i < linePoints.length; i++)
        {
            const point = linePoints[i] as SegmentPoint;

            // Cheap remove colinear
            if (removeColinear && last && i < linePoints.length - 1)
            {
                let a = Math.atan2(last.y - point.y, last.x - point.x);

                if (a < 0) a += doublePI;

                if (a > lastAngle - removeColinear && a < lastAngle + removeColinear) continue;
                lastAngle = a;
            }
            resultPoints.push(point);
            last = point;
        }

        return resultPoints;
    }

    public static getPointsFromLine(line: Bezier, precision = 20, removeColinear = 0): SegmentPoint[]
    {
        const resultPoints = [] as SegmentPoint[];
        const doublePI = Math.PI * 2;
        // const linePoints = line.getLUT(precision);

        let last: SegmentPoint | null = null;
        let lastAngle = Number.POSITIVE_INFINITY;

        for (let i = 0; i <= precision; i++)
        {
            const step = (1 / precision) * i;
            const point = line.get(step) as SegmentPoint;
            const normal = line.normal(step);

            // Somehow normals from Bezier library can be NaN
            point.nx = isNaN(normal.x) ? 0 : normal.x;
            point.ny = isNaN(normal.y) ? 1 : normal.y;
            point.a = Math.atan2(point.ny, point.nx);

            // Cheap remove colinear
            if (removeColinear && last && i <= precision - 1)
            {
                let a = Math.atan2(last.y - point.y, last.x - point.x);

                if (a < 0) a += doublePI;

                if (a > lastAngle - removeColinear && a < lastAngle + removeColinear) continue;
                lastAngle = a;
            }

            last = point;
            resultPoints.push(point);
        }

        return resultPoints;
    }

    public static getPointsFromLines(lines: Bezier[], precision = 20, removeColinear = 0): SegmentPoint[]
    {
        let resultPoints = [] as SegmentPoint[];

        for (const line of lines)
        {
            const points = this.getPointsFromLine(line, precision, removeColinear);

            resultPoints = resultPoints.concat(points);
        }

        return resultPoints;
    }

    public static getPointsFromVerts(
        verts: SegmentVert[], absControl = false,
        precision = 20, removeColinear = 0,
    ): SegmentPoint[]
    {
        const lines = this.getLines(verts, absControl);

        return this.getPointsFromLines(lines, precision, removeColinear);
    }

    public static getPointsFromVertsWithInterval(
        verts: SegmentVert[], absControl = false, interval = 300, removeColinear = 0,
    ): SegmentPoint[]
    {
        const lines = this.getLines(verts, absControl);

        let resultPoints = [] as SegmentPoint[];

        for (const line of lines)
        {
            const length = line.length();
            const precision = Math.round(length / interval);

            if (precision <= 1) continue;

            const points = this.getPointsFromLine(line, precision, removeColinear);

            resultPoints = resultPoints.concat(points);
        }

        const clean = resultPoints.slice(0);

        // Remove points that are too close to each other
        for (let i = 1; i < resultPoints.length; i++)
        {
            const prev = resultPoints[i - 1];
            const point = resultPoints[i];

            if (Math.abs(point.x - prev.x) < 1 && Math.abs(point.y - prev.y) < 1)
            {
                clean.splice(clean.indexOf(prev), 1);
            }
        }

        return clean;
    }
}
